import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import TimeInput from '../../components/ui/TimeInput';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { hourmeterService } from '../../services/api';
import type { HourmeterDetail, HourmeterUpdateData } from '../../types';

interface HourmeterFormData {
  date: string;
  initialHour: string;
  finalHour: string;
  horasTrabajo: string;
  standby: boolean;
  accesorio: boolean;
  horasTrabajoAccesorio: number;
  enterHour: string;
  outHour: string;
  observaciones: string;
  standByClient: number;
  standByAlly: number;
}

const formatHourmeter = (value: string): string => {
  if (!value) return '';
  const parts = value.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.join(',');
};

const getNormalizedDateString = (date: Date) => {
  const timezoneOffsetInMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffsetInMs).toISOString().split('T')[0];
};

const formatDateForDisplay = (value: string) => {
  if (!value) {
    return 'Selecciona una fecha';
  }

  const parsedDate = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return 'Fecha inválida';
  }

  return parsedDate.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const extractTimeFromISO = (value?: string | null): string => {
  if (!value) return '';
  const [_, timePart] = value.split('T');
  if (!timePart) return '';
  return timePart.slice(0, 5);
};

const createInitialFormData = (dateOverride?: string): HourmeterFormData => ({
  date: dateOverride ?? getNormalizedDateString(new Date()),
  initialHour: '',
  finalHour: '',
  horasTrabajo: '',
  standby: false,
  accesorio: false,
  horasTrabajoAccesorio: 0,
  enterHour: '',
  outHour: '',
  observaciones: '',
  standByClient: 0,
  standByAlly: 0,
});

const mapHourmeterToFormData = (hourmeter: HourmeterDetail): HourmeterFormData => {
  const datePart = hourmeter.date?.includes('T')
    ? hourmeter.date.split('T')[0]
    : hourmeter.date;

  const initialHourValue = Number(hourmeter.initialHour);
  const finalHourValue = Number(hourmeter.finalHour);

  const operatorObservations =
    (hourmeter as any).operatorObservations ?? hourmeter.residentObservations ?? '';

  return {
    date: datePart || getNormalizedDateString(new Date()),
    initialHour: Number.isFinite(initialHourValue) ? initialHourValue.toString() : '',
    finalHour: Number.isFinite(finalHourValue) ? finalHourValue.toString() : '',
    horasTrabajo: '',
    standby: Boolean((hourmeter as any).standby ?? hourmeter.standByClient),
    accesorio: Boolean((hourmeter as any).accesorio),
    horasTrabajoAccesorio: (hourmeter as any).horasTrabajoAccesorio ?? 0,
    enterHour: extractTimeFromISO(hourmeter.startDate),
    outHour: extractTimeFromISO(hourmeter.finalDate),
    observaciones: operatorObservations,
    standByClient: Number((hourmeter as any).standByClient) || 0,
    standByAlly: Number((hourmeter as any).standByAlly) || 0,
  };
};

const buildUpdatePayload = (data: HourmeterFormData): Partial<HourmeterUpdateData> => {
  const payload: Partial<HourmeterUpdateData> = {};
  const initialHourValue = Number(data.initialHour);
  const finalHourValue = Number(data.finalHour);

  if (!Number.isNaN(initialHourValue)) {
    payload.initialHour = initialHourValue;
  }
  if (!Number.isNaN(finalHourValue)) {
    payload.finalHour = finalHourValue;
  }
  if (data.date) {
    payload.date = data.date;
  }
  if (data.enterHour) {
    payload.startDate = `${data.date}T${data.enterHour}`;
  }
  if (data.outHour) {
    payload.finalDate = `${data.date}T${data.outHour}`;
  }
  if (data.observaciones !== undefined) {
    payload.operatorObservations = data.observaciones;
  }
  if (data.standby !== undefined) {
    payload.standByClient = data.standby ? data.standByClient : 0;
    payload.standByAlly = data.standby ? data.standByAlly : 0;
  }

  return payload;
};

const EditHourmeter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { operator } = useAuth();
  const originalPayloadRef = useRef<Partial<HourmeterUpdateData>>({});
  const originalStandbyRef = useRef<{ standByClient: number; standByAlly: number; standby: boolean }>({
    standByClient: 0,
    standByAlly: 0,
    standby: false,
  });

  const [hourmeter, setHourmeter] = useState<HourmeterDetail | null>(null);
  const [formData, setFormData] = useState<HourmeterFormData>(() => createInitialFormData());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchHourmeter = async () => {
      if (!id || !operator) return;

      try {
        setLoading(true);
        setError(null);
        const userRole = operator.role || 'operator';
        const data = await hourmeterService.getHourmeterForReceipt(parseInt(id), operator.id, userRole);
        setHourmeter(data);
        const mapped = mapHourmeterToFormData(data);
        setFormData(mapped);
        originalPayloadRef.current = buildUpdatePayload(mapped);
        originalStandbyRef.current = {
          standByClient: mapped.standByClient,
          standByAlly: mapped.standByAlly,
          standby: mapped.standby,
        };
      } catch (err) {
        console.error('Error fetching hourmeter:', err);
        setError('Error al cargar los datos del horómetro');
      } finally {
        setLoading(false);
      }
    };

    fetchHourmeter();
  }, [id, operator]);

  useEffect(() => {
    const { initialHour, finalHour } = formData;

    if (!initialHour || !finalHour) {
      setFormData((prev) =>
        prev.horasTrabajo === '' ? prev : { ...prev, horasTrabajo: '' }
      );
      return;
    }

    const parsedInitial = Number(initialHour);
    const parsedFinal = Number(finalHour);

    if (Number.isNaN(parsedInitial) || Number.isNaN(parsedFinal)) {
      return;
    }

    const rawDiff = parsedFinal - parsedInitial;
    const roundedDiff = Math.round(rawDiff * 100) / 100;
    const calculatedHours = Number.isFinite(roundedDiff) ? roundedDiff.toString() : '';

    if (Number(calculatedHours) > 0) {
      setFormData((prev) =>
        prev.horasTrabajo === calculatedHours ? prev : { ...prev, horasTrabajo: calculatedHours }
      );
    } else {
      setFormData((prev) =>
        prev.horasTrabajo === '' ? prev : { ...prev, horasTrabajo: '' }
      );
    }
  }, [formData.initialHour, formData.finalHour]);

  const handleChange = (field: keyof HourmeterFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = event.target;
    const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';
    const value = isCheckbox ? target.checked : target.value;

    setFormData((prev) => {
      const updated: HourmeterFormData = { ...prev };

      if (field === 'horasTrabajoAccesorio') {
        updated.horasTrabajoAccesorio = Number(value) || 0;
      } else if (field === 'standByClient' || field === 'standByAlly') {
        updated[field] = Number(value) || 0;
      } else if (field === 'standby') {
        const shouldEnable = Boolean(value);
        updated.standby = shouldEnable;
        if (shouldEnable) {
          updated.standByClient = originalStandbyRef.current.standByClient;
          updated.standByAlly = originalStandbyRef.current.standByAlly;
        } else {
          updated.standByClient = 0;
          updated.standByAlly = 0;
        }
      } else if (field === 'accesorio') {
        updated[field] = Boolean(value) as HourmeterFormData[typeof field];
      } else {
        updated[field] = (value as string) as HourmeterFormData[typeof field];
      }

      if (field === 'accesorio' && value === false) {
        updated.horasTrabajoAccesorio = 0;
      }

      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!operator || !hourmeter) return;

    const initialHourValue = Number(formData.initialHour);
    const finalHourValue = Number(formData.finalHour);

    if (Number.isNaN(initialHourValue) || Number.isNaN(finalHourValue)) {
      setError('Los valores de horómetro deben ser numéricos.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = buildUpdatePayload(formData);
      const updates: Partial<HourmeterUpdateData> = {};

      (Object.entries(payload) as [keyof HourmeterUpdateData, any][]).forEach(([key, value]) => {
        if (value !== originalPayloadRef.current[key]) {
          updates[key] = value;
        }
      });

      if (Object.keys(updates).length === 0) {
        setError('No se han realizado cambios.');
        return;
      }

      const response = await hourmeterService.updateHourmeter(
        hourmeter.id,
        operator.id,
        updates
      );

      setSuccess(response.message || 'Horómetro actualizado exitosamente.');
      const updatedHourmeter = response.hourmeter ?? hourmeter;
      setHourmeter(updatedHourmeter);
      const mapped = mapHourmeterToFormData(updatedHourmeter);
      setFormData(mapped);
      originalPayloadRef.current = buildUpdatePayload(mapped);
      originalStandbyRef.current = {
        standByClient: mapped.standByClient,
        standByAlly: mapped.standByAlly,
        standby: mapped.standby,
      };

      setTimeout(() => {
        navigate('/dashboard', {
          state: {
            success: true,
            message: 'Horómetro corregido. Ahora puede ser firmado nuevamente.',
          },
        });
      }, 2000);
    } catch (err: any) {
      console.error('Error updating hourmeter:', err);
      setError(err.response?.data?.message || 'Error al actualizar el horómetro.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <Loading size="lg" text="Cargando datos del horómetro..." />
      </div>
    );
  }

  if (!hourmeter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Horómetro no encontrado</h2>
          <Button onClick={() => navigate('/dashboard')}>Volver al Registro</Button>
        </div>
      </div>
    );
  }

  const isFormDisabled = saving;

  return (
    <div className="min-h-screen bg-gray-200">
      {/* Header */}
      <header className="bg-retri-blue shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:text-blue-200 text-sm font-medium"
          >
            ← Volver
          </button>
          <div className="h-4 w-px bg-blue-300" />
          <h1 className="text-white font-bold text-base">Editar horómetro</h1>
        </div>
      </header>

      <div className="py-6 px-4 sm:px-8">
      <div className="max-w-5xl mx-auto">

        {hourmeter.isRejected && (
          <Alert
            variant="warning"
            className="mb-6"
          >
            <div>
              <h3 className="font-semibold">Horómetro rechazado</h3>
              <p className="text-sm mt-1">
                <strong>Fecha de rechazo:</strong>{' '}
                {hourmeter.residentRejectDate
                  ? new Intl.DateTimeFormat('es-ES', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    }).format(new Date(hourmeter.residentRejectDate))
                  : 'N/A'}
              </p>
              <p className="text-sm mt-1">
                <strong>Comentarios del residente:</strong>{' '}
                {hourmeter.residentRejectComments || 'Sin comentarios'}
              </p>
              {hourmeter.editedByOperator && (
                <p className="text-sm mt-1">
                  <strong>Última edición:</strong>{' '}
                  {new Intl.DateTimeFormat('es-ES', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }).format(new Date(hourmeter.editedByOperator))}
                </p>
              )}
            </div>
          </Alert>
        )}

        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

        <Card>
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl text-gray-900">Editar horómetro</CardTitle>
            <CardDescription>
              Ajusta los mismos campos que usas al crear el registro. Todos los valores vienen prellenados con la información del horómetro #{hourmeter.id}.
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="pt-6 space-y-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex flex-col gap-2 col-span-2">
                  <span className="text-sm font-semibold text-gray-800">Fecha</span>
                  <div className="flex items-center justify-center rounded-lg border-2 border-gray-400 bg-white px-3 py-2 shadow-sm text-sm font-semibold text-gray-900">
                    {formatDateForDisplay(formData.date)}
                  </div>
                </div>

                <Input
                  type="text"
                  inputMode="decimal"
                  label="Horómetro inicial"
                  placeholder="Ej: 1.200,5"
                  value={formatHourmeter(formData.initialHour)}
                  onChange={handleChange('initialHour')}
                  required
                  disabled={isFormDisabled}
                  onInput={(e) => {
                    e.currentTarget.value = e.currentTarget.value.replace(/[^\d,]/g, '').replace(',', '.');
                  }}
                />

                <Input
                  type="text"
                  inputMode="decimal"
                  label="Horómetro final"
                  placeholder="Ej: 1.215,3"
                  value={formatHourmeter(formData.finalHour)}
                  onChange={handleChange('finalHour')}
                  required
                  disabled={isFormDisabled}
                  onInput={(e) => {
                    e.currentTarget.value = e.currentTarget.value.replace(/[^\d,]/g, '').replace(',', '.');
                  }}
                />

                <Input
                  type="number"
                  label="Horas trabajadas"
                  placeholder="Ej: 8"
                  value={formData.horasTrabajo}
                  onChange={handleChange('horasTrabajo')}
                  min="0"
                  step="0.1"
                  required
                  disabled={isFormDisabled}
                />

                <div className="md:col-span-2 flex items-center space-x-3 ">
                  <input
                    id="standby"
                    type="checkbox"
                    className="h-4 w-4 text-retri-blue focus:ring-retri-blue border-gray-300 rounded"
                    checked={formData.standby}
                    onChange={handleChange('standby')}
                    disabled={isFormDisabled}
                  />
                  <label htmlFor="standby" className="text-sm font-semibold text-gray-800">
                    ¿Stand by?
                  </label>
                </div>

                <TimeInput
                  label="Hora de entrada"
                  value={formData.enterHour}
                  onChange={(value) => setFormData((prev) => ({ ...prev, enterHour: value }))}
                  disabled={isFormDisabled}
                  className="col-span-2 md:col-span-1"
                />

                <TimeInput
                  label="Hora de salida"
                  value={formData.outHour}
                  onChange={(value) => setFormData((prev) => ({ ...prev, outHour: value }))}
                  disabled={isFormDisabled}
                  className="col-span-2 md:col-span-1"
                />

                <div className="col-span-2 flex flex-row gap-3">
                  <div className="flex items-center space-x-3 w-full">
                    <input
                      id="accesorio"
                      type="checkbox"
                      className="h-4 w-4 text-retri-blue focus:ring-retri-blue border-gray-300 rounded"
                      checked={formData.accesorio}
                      onChange={handleChange('accesorio')}
                      disabled
                    />
                    <label htmlFor="accesorio" className="text-sm font-semibold text-gray-800">
                      ¿Accesorio?
                    </label>
                  </div>

                  {formData.accesorio && (
                    <Input
                      type="number"
                      label="Horas Accesorio"
                      placeholder="Ej: 8"
                      value={formData.horasTrabajoAccesorio.toString()}
                      onChange={handleChange('horasTrabajoAccesorio')}
                      min="0"
                      step="0.1"
                      required
                      disabled
                      className="w-full"
                    />
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <label htmlFor="observaciones" className="text-sm font-semibold text-gray-800 mb-2">
                  Observaciones
                </label>
                <textarea
                  id="observaciones"
                  value={formData.observaciones}
                  onChange={handleChange('observaciones')}
                  placeholder="Añade observaciones relevantes para este horómetro"
                  disabled={isFormDisabled}
                  className="min-h-[120px] rounded-md border-2 border-gray-400 bg-white px-3 py-2 text-base text-gray-900 placeholder:text-gray-500 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-retri-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </CardContent>

            <CardFooter className="justify-end gap-3">
              <Button type="button" variant="ghost" onClick={() => navigate('/dashboard')} disabled={isFormDisabled}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={isFormDisabled}>
                {isFormDisabled ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default EditHourmeter;
